import React, { useState } from 'react'
import { cn } from '@/utils/cn'

interface TabItem {
  id: string
  label: string
  content: React.ReactNode
  icon?: React.ReactNode
  badge?: string | number
  disabled?: boolean
}

interface TabsProps {
  tabs: TabItem[]
  defaultTab?: string
  onChange?: (tabId: string) => void
  variant?: 'default' | 'pills' | 'underline'
  size?: 'sm' | 'md' | 'lg'
  className?: string
}

export function Tabs({
  tabs,
  defaultTab,
  onChange,
  variant = 'default',
  size = 'md',
  className
}: TabsProps) {
  const [activeTab, setActiveTab] = useState(defaultTab || tabs[0]?.id)

  const handleTabChange = (tabId: string) => {
    if (tabs.find(tab => tab.id === tabId)?.disabled) return
    setActiveTab(tabId)
    onChange?.(tabId)
  }

  const getTabClasses = (tab: TabItem, isActive: boolean) => {
    const baseClasses = "inline-flex items-center gap-2 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:ring-offset-2"

    const sizeClasses = {
      sm: "px-3 py-2 text-sm font-medium",
      md: "px-4 py-3 text-sm font-medium",
      lg: "px-6 py-4 text-base font-medium"
    }

    const variantClasses = {
      default: {
        base: "border-b-2 -mb-px",
        active: "border-primary-600 text-primary-600",
        inactive: "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
      },
      pills: {
        base: "rounded-lg",
        active: "bg-primary-600 text-white",
        inactive: "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
      },
      underline: {
        base: "border-b-2 -mb-px",
        active: "border-primary-600 text-primary-600 bg-primary-50",
        inactive: "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 hover:bg-gray-50"
      }
    }

    const disabledClasses = tab.disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"

    return cn(
      baseClasses,
      sizeClasses[size],
      variantClasses[variant].base,
      isActive ? variantClasses[variant].active : variantClasses[variant].inactive,
      disabledClasses
    )
  }

  const activeTabContent = tabs.find(tab => tab.id === activeTab)?.content

  return (
    <div className={cn("w-full", className)}>
      {/* Tab Navigation */}
      <div className={cn(
        "flex space-x-1",
        variant === 'default' || variant === 'underline' ? "border-b border-gray-200" : ""
      )}>
        {tabs.map((tab) => {
          const isActive = tab.id === activeTab

          return (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={getTabClasses(tab, isActive)}
              disabled={tab.disabled}
              role="tab"
              aria-selected={isActive}
              aria-controls={`tabpanel-${tab.id}`}
            >
              {tab.icon && (
                <span className="flex-shrink-0">
                  {tab.icon}
                </span>
              )}
              <span>{tab.label}</span>
              {tab.badge && (
                <span className={cn(
                  "inline-flex items-center justify-center px-2 py-1 text-xs font-bold rounded-full",
                  isActive
                    ? "bg-white/20 text-white"
                    : "bg-gray-100 text-gray-600"
                )}>
                  {tab.badge}
                </span>
              )}
            </button>
          )
        })}
      </div>

      {/* Tab Content */}
      <div
        id={`tabpanel-${activeTab}`}
        role="tabpanel"
        aria-labelledby={`tab-${activeTab}`}
        className="mt-6"
      >
        {activeTabContent}
      </div>
    </div>
  )
}

// Convenience hook for managing tab state
export function useTabs(tabs: TabItem[], defaultTab?: string) {
  const [activeTab, setActiveTab] = useState(defaultTab || tabs[0]?.id)

  const changeTab = (tabId: string) => {
    setActiveTab(tabId)
  }

  const activeTabData = tabs.find(tab => tab.id === activeTab)

  return {
    activeTab,
    changeTab,
    activeTabData
  }
}